import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { useSearchParams } from 'react-router-dom';
import { NavigationPane } from './NavigationPane';
import { ChatbotPane } from './ChatbotPane';
import { SessionStatus } from '../common/SessionStatus';
import { UploadProgressBar } from '../upload/UploadProgressBar';
import { useTheme } from '../../theme/ThemeProvider';
import { useCallContext } from '../../contexts/CallContext';

interface AppShellProps {
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [chatbotWidth, setChatbotWidth] = useState(400);
  const { isDarkMode, toggleTheme, brandingSettings } = useTheme();
  const [searchParams] = useSearchParams();
  // Chatbot open state lives in context so pages (e.g. "Ask AI about these results")
  // can open the pane too.
  const { callContext, isChatbotOpen, setIsChatbotOpen } = useCallContext();

  // Show tenant indicator when PlatformAdmin is viewing a specific tenant
  const activeTenantParam = searchParams.get('tenant');

  const handleNavToggle = () => {
    setIsNavExpanded(!isNavExpanded);
  };

  const handleChatbotToggle = () => {
    setIsChatbotOpen(!isChatbotOpen);
  };

  const handleChatbotWidthChange = useCallback((width: number) => {
    setChatbotWidth(width);
  }, []);

  // Calculate margins to fit content between drawers
  const navWidth = isNavExpanded ? 240 : 64;
  const chatbotMargin = isChatbotOpen ? chatbotWidth : 0;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Pane */}
      <NavigationPane isExpanded={isNavExpanded} onToggle={handleNavToggle} />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          ml: `${navWidth}px`,
          mr: `${chatbotMargin}px`,
          transition: 'margin 0.3s ease',
          minWidth: 0, // Prevents content from overflowing
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="static"
          elevation={1}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              {brandingSettings?.logoUrl && (
                <Avatar
                  src={brandingSettings.logoUrl}
                  alt={brandingSettings.companyName}
                  variant="square"
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    mr: 2,
                    objectFit: 'contain',
                    '& img': {
                      objectFit: 'contain'
                    }
                  }}
                />
              )}
              <Typography variant="h6" component="div">
                {brandingSettings?.applicationTitle || 'Your City Emergency Communications'}
              </Typography>
              {activeTenantParam && (
                <Chip
                  label={`Tenant: ${activeTenantParam}`}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <UploadProgressBar />
              <SessionStatus />
              <IconButton
                onClick={handleChatbotToggle}
                color="inherit"
                aria-label={isChatbotOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
              >
                <AutoAwesome />
              </IconButton>
              <IconButton
                onClick={toggleTheme}
                color="inherit"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            overflow: 'auto',
            bgcolor: 'background.default',
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Chatbot Pane */}
      <ChatbotPane
        isExpanded={isChatbotOpen}
        onToggle={handleChatbotToggle}
        onWidthChange={handleChatbotWidthChange}
        callContext={callContext}
      />
    </Box>
  );
};